import styled from '@emotion/styled';
import { Button, Empty, Result, Spin } from 'antd';
import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { PageTitle } from '../components/common/page-title';
import { CourseCarousel } from '../components/play-course/course-carousel';
import { useHandleLoadPlaylist } from '../hooks/playlists';
import { usePlayListPathId } from '../hooks/use-playlist-path-id';
import { usePlaylistCurrentSelector } from '../state/playlists/selectors';
import { Paths } from '../types/common';
import { TListedPlaylist } from '../types/responses/video';

const CACHE_PREFIX = 'playcourse-detail-cache';

const getCacheKey = (id: string): string => `${CACHE_PREFIX}:${id}`;

const readCachedCourse = (id: string | undefined): TListedPlaylist | null => {
  if (!id) return null;

  try {
    const cached = sessionStorage.getItem(getCacheKey(id));
    return cached ? (JSON.parse(cached) as TListedPlaylist) : null;
  } catch {
    return null;
  }
};

const writeCachedCourse = (course: TListedPlaylist): void => {
  try {
    sessionStorage.setItem(getCacheKey(course.id), JSON.stringify(course));
  } catch {
    // Ignore quota/private-mode failures; the live Redux state is still primary.
  }
};

const PlayCourseShell = styled('div')`
  --play-course-mobile-header-height: 48px;
  --play-course-mobile-chrome-reclaimed-height: calc(
    var(--app-mobile-menu-height, 68px) +
      var(--play-course-mobile-header-height)
  );
`;

const HeaderRow = styled('div')`
  display: flex;
  align-items: baseline;
  justify-content: flex-start;
  gap: 16px;
  flex-wrap: wrap;

  @media (max-width: 948px), (pointer: coarse) {
    max-height: var(--play-course-mobile-header-height);
    overflow: hidden;
    transition:
      opacity 0.22s ease,
      transform 0.34s cubic-bezier(0.2, 0.82, 0.18, 1);

    main[data-mobile-chrome-visible='false'] & {
      opacity: 0;
      pointer-events: none;
      transform: translateY(-130%);
    }
  }
`;

export const PlayCourseDetailPage: FC = () => {
  const id = usePlayListPathId();
  const navigate = useNavigate();
  const handleLoadPlaylist = useHandleLoadPlaylist();
  const { data, pending, error } = usePlaylistCurrentSelector();
  const [cachedCourse, setCachedCourse] = useState<TListedPlaylist | null>(() =>
    readCachedCourse(id)
  );

  useEffect(() => {
    if (id) {
      setCachedCourse(readCachedCourse(id));
      void handleLoadPlaylist(id);
    }
  }, [id, handleLoadPlaylist]);

  useEffect(() => {
    if (data && data.id === id) {
      writeCachedCourse(data);
      setCachedCourse(data);
    }
  }, [data, id]);

  const isCurrentPlaylist = Boolean(id && data?.id === id);
  const displayData = useMemo(() => {
    if (isCurrentPlaylist) return data;
    if (cachedCourse?.id === id) return cachedCourse;
    return null;
  }, [cachedCourse, data, id, isCurrentPlaylist]);

  if (pending && !displayData) {
    return <Spin />;
  }

  if (error && !displayData) {
    return (
      <Result
        status="warning"
        title="Course is unavailable"
        subTitle="The course could not be loaded."
        extra={
          <Button onClick={() => navigate(Paths.PLAYCOURSE)}>
            Back to PlayCourse
          </Button>
        }
      />
    );
  }

  if (!id || (!pending && !displayData)) {
    return (
      <Result
        status="warning"
        title="Course is unavailable"
        subTitle="The course could not be loaded."
        extra={
          <Button onClick={() => navigate(Paths.PLAYCOURSE)}>
            Back to PlayCourse
          </Button>
        }
      />
    );
  }

  if (!displayData) {
    return <Spin />;
  }

  return (
    <PlayCourseShell>
      <HeaderRow>
        <PageTitle>{displayData.title}</PageTitle>
      </HeaderRow>
      {displayData.items.length ? (
        <CourseCarousel
          courseStructure={displayData.courseStructure}
          items={displayData.items}
          playlistId={displayData.id}
        />
      ) : (
        <Empty description="No materials in this course yet" />
      )}
    </PlayCourseShell>
  );
};
